/*
 * /board-game-bank — the page: which screen, who owns the game, where it is
 * kept, how phones join.
 *
 * Roles
 *   host  — this device holds the game (one-device mode, or the banker of a
 *           room). Every change runs G.dispatch here, is saved to
 *           localStorage and, in a room, sent to every phone.
 *   guest — a player's phone in a room (?room=CODE). It renders what the
 *           host sends and turns buttons into commands.
 *
 * A reload keeps everything: the host's game and room code are in
 * localStorage (the room is re-opened under the SAME code, so the phones
 * find it again), a phone's seat is kept per room code on the phone.
 */
import * as L from './ledger.js';
import * as G from './game.js';
import * as N from './net.js';
import * as H from './history.js';
import * as S from './sheet.js';
import * as Setup from './setup.js';
import * as Table from './table.js';
import * as Wallet from './wallet.js';
import { t, $, h, clear, put, money, toast, stage, diag, sound, buzz, setSound, soundEnabled, settingLoad, settingSave, SLUG } from './ui.js';

const STORE = 'bgb:game';
const RESUME_MS = 12 * 3600 * 1000;

const app = {
    role: null,
    game: null,
    me: null,
    online: new Set(),
    conn: '',
    code: '',
    hostRoom: null,
    roomStatus: '',
    guest: null,
    flash: {},
    hfilter: '',
    hshow: 60,
    lastLvl: null,
    lastDelta: null,
    completed: false,
};
window.__bgbApp = app;    // for the e2e checks: read-only use

/* ── storage ──────────────────────────────────────────────────────────────── */

function save() {
    if (app.role !== 'host' || !app.game) return;
    try { localStorage.setItem(STORE, JSON.stringify(app.game)); } catch (e) {
        if (!save.warned) { save.warned = true; toast(t('err.storage'), 'warning'); diag('storage_failed', { name: e && e.name }); }
    }
}
function load() {
    try {
        const g = JSON.parse(localStorage.getItem(STORE) || 'null');
        if (g && g.v === 1 && Array.isArray(g.players) && Array.isArray(g.log) && g.players.length >= 1) return g;
    } catch (e) { /* a broken record is the same as none */ }
    return null;
}
function forget() { try { localStorage.removeItem(STORE); } catch (e) { /* nothing to forget */ } }

/* ── screens ──────────────────────────────────────────────────────────────── */

function show(view) {
    $('bgbSetup').hidden = view !== 'setup';
    $('bgbJoinBox').hidden = view !== 'setup';
    $('bgbTable').hidden = view !== 'table';
    $('bgbWallet').hidden = view !== 'wallet';
    $('bgbApp').setAttribute('data-view', view);
    if (view !== 'setup') $('bgbResume').hidden = true;
}

function render() {
    if (app.role === 'host') Table.render(app);
    else if (app.role === 'guest') Wallet.render(app);
}

/* ── messages ─────────────────────────────────────────────────────────────── */

app.say = (msg, type) => toast(msg, type);

app.errText = (r) => {
    const g = app.game;
    const nm = (pid) => (pid ? H.name(g, pid) : '');
    if (!r) return t('err.generic');
    switch (r.err) {
        case 'funds': return t('err.funds', { name: nm(r.pid), x: money(r.have || 0, g.cur) });
        case 'out': return t('err.out', { name: nm(r.pid) });
        case 'void_breaks': return t('err.void_breaks', { name: nm(r.pid), n: r.at });
        case 'raise_min': return t('err.raise_min', { x: r.min });
        case 'raise_max': return t('err.raise_max', { x: r.max });
        case 'turn': return t('err.turn', { name: g && g.poker && g.poker.hand ? nm(g.poker.hand.toAct) : '' });
        default: {
            const k = 'err.' + (r.err || 'generic');
            const s = t(k);
            return s === k ? t('err.generic') : s;
        }
    }
};

/* ── running a command ────────────────────────────────────────────────────── */

function balances(g) { return g ? L.replay(g).bal : {}; }

function commit(r, meta) {
    const before = app.game;
    app.game = r.game;
    save();
    // what changed, for the flash and the sound
    const b0 = balances(before), b1 = balances(app.game);
    let moved = false;
    for (const pid of Object.keys(b1)) if (b0[pid] !== b1[pid]) { app.flash[pid] = true; moved = true; }
    const reqs0 = before ? before.requests.filter((x) => x.st === 'open').length : 0;
    const reqs1 = app.game.requests.filter((x) => x.st === 'open').length;
    if (meta && meta.remote && reqs1 > reqs0) { sound('alert'); }
    else if (moved) sound('in');
    if (!before || before.log.filter((e) => !e.void).length === 0) {
        if (app.game.log.length) stage('first_operation', { kind: app.game.kind });
    }
    if (app.game.kind === 'poker' && app.game.poker.handNo === 1 && (!before || before.poker.handNo === 0)) stage('first_hand');
    render();
    if (app.hostRoom) app.hostRoom.broadcast();
}

app.run = (cmd) => {
    if (app.role === 'guest') {
        if (!app.guest) return Promise.resolve({ ok: false, err: 'offline' });
        return app.guest.command(cmd).then((r) => {
            if (!r.ok) { sound('bad'); if (!app.dialogOpen()) app.say(app.errText(r), 'warning'); }
            return r;
        });
    }
    if (app.role !== 'host' || !app.game) return Promise.resolve({ ok: false, err: 'generic' });
    const r = G.dispatch(app.game, cmd, { host: true }, Date.now());
    if (r.ok) commit(r);
    return Promise.resolve(r);
};

app.dialogOpen = () => { const d = $('bgbDlg'); return !!(d && d.open); };

app.undo = async () => {
    const g = app.game;
    const what = (() => {
        if (g.kind === 'poker' && g.poker.hand) return t('pk.undo_action');
        for (let i = g.log.length - 1; i >= 0; i--) if (!g.log[i].void) return H.describe(g, g.log[i], L.replay(g)).what;
        return '';
    })();
    const r = await app.run({ type: 'undo' });
    app.say(r.ok ? t('ui.undone', { what }) : app.errText(r), r.ok ? 'info' : 'warning');
};
app.voidEntry = async (id) => {
    const r = await app.run({ type: 'void', id });
    if (!r.ok) app.say(app.errText(r), 'warning');
};
app.restoreEntry = async (id) => {
    const r = await app.run({ type: 'restore', id });
    if (!r.ok) app.say(app.errText(r), 'warning');
};
app.answer = async (rid, yes) => {
    const r = await app.run({ type: yes ? 'req_ok' : 'req_no', rid });
    if (!r.ok) app.say(app.errText(r), 'warning');
};
app.act = async (pid, a, to) => {
    const r = await app.run({ type: 'act', pid, a, to });
    if (!r.ok) app.say(app.errText(r), 'warning');
};

/* ── host: game lifecycle ─────────────────────────────────────────────────── */

app.startGame = (g) => {
    app.role = 'host';
    app.game = g;
    app.completed = false;
    save();
    show('table');
    render();
    wake();
};

function resumeHost(g) {
    app.role = 'host';
    app.game = g;
    show('table');
    if (g.room && g.room.code) startHosting(g.room.code);
    render();
    stage('resumed', { entries: g.log.length, room: !!g.room });
}

app.endGame = async () => {
    const g = app.game;
    if (g.kind === 'poker' && g.poker.hand) { app.say(t('err.in_hand'), 'warning'); return; }
    const ok = await S.confirm(t('ui.end_title'), t('ui.end_text'), t('ui.end_game'));
    if (!ok) return;
    const r = await app.run({ type: 'end' });
    if (!r.ok) { app.say(app.errText(r), 'warning'); return; }
    complete('end');
    stage('game_ended');
    try { if (window.__wakeLock) window.__wakeLock.release(); } catch (e) { /* nothing held */ }
};

function complete(how, fmt) {
    if (app.completed) return;
    app.completed = true;
    const g = app.game;
    try {
        if (typeof window.__aeToolComplete === 'function') {
            window.__aeToolComplete({
                kind: g.kind, preset: g.preset, how, format: fmt || null,
                players: g.players.length, entries: g.log.filter((e) => !e.void).length,
                hands: g.poker ? g.poker.handNo : 0, room: !!g.room,
                minutes: Math.round(((g.updated || Date.now()) - (g.created || Date.now())) / 60000),
            });
        }
    } catch (e) { /* telemetry must not break the bank */ }
}

app.newGame = async () => {
    if (app.game && !app.game.ended && app.game.log.length) {
        const ok = await S.confirm(t('ui.new_title'), t('ui.new_text'), t('res.new'), true);
        if (!ok) return;
    }
    stopHosting();
    forget();
    app.role = null;
    app.game = null;
    Setup.render(app);
    show('setup');
};

/* ── host: the room ───────────────────────────────────────────────────────── */

function startHosting(code) {
    if (!N.available()) { app.say(t('err.room_lib'), 'error'); diag('room_lib_missing'); return false; }
    app.game.room = { code };
    save();
    app.roomStatus = 'opening';
    app.hostRoom = N.createHost(code, {
        game: () => app.game,
        view: () => G.publicView(app.game),
        command: (cmd, actor) => {
            const r = G.dispatch(app.game, cmd, actor, Date.now());
            if (r.ok) commit(r, { remote: true, from: actor.pid });
            return r;
        },
        seat: (dev, pid) => {
            const g = L.clone(app.game);
            const r = N.claimSeat(g, pid, dev);
            if (r.ok) { app.game = g; save(); if (pid) stage('seat_claimed'); }
            return r;
        },
        status: (kind, detail) => {
            app.roomStatus = kind;
            if (kind === 'error') diag('room_host_error', { type: String(detail || '') });
            if (kind === 'open') stage('room_open');
            render();
        },
        peers: () => {
            if (!app.hostRoom) return;
            app.online = app.hostRoom.online();
            render();
            app.hostRoom.broadcast();
        },
    });
    return true;
}

function stopHosting() {
    if (app.hostRoom) { app.hostRoom.stop(); app.hostRoom = null; }
    app.online = new Set();
    app.roomStatus = '';
    if (app.game && app.game.room) { delete app.game.room; save(); }
}

app.toggleRoom = async () => {
    if (app.hostRoom) {
        const ok = await S.confirm(t('inv.close_title'), t('inv.close_text'), t('inv.close'));
        if (!ok) return;
        stopHosting();
        render();
        return;
    }
    const code = (window.__RoomConnect && window.__RoomConnect.generateCode(6)) || '';
    if (!code) { app.say(t('err.room_lib'), 'error'); return; }
    if (startHosting(code)) render();
};

app.inviteUrl = (code) => {
    const u = location.origin + location.pathname + '?room=' + encodeURIComponent(code);
    return typeof window.__withRef === 'function' ? window.__withRef(u) : u;
};

/* ── guest ────────────────────────────────────────────────────────────────── */

function joinRoom(code) {
    app.role = 'guest';
    app.code = code;
    app.game = null;
    app.me = null;
    app.conn = 'connecting';
    try { history.replaceState(null, '', location.pathname + '?room=' + encodeURIComponent(code)); } catch (e) { /* file:// or sandbox */ }
    show('wallet');
    Wallet.render(app);
    if (!N.available()) { app.conn = 'error'; Wallet.render(app); diag('room_lib_missing'); return; }
    let joinedOnce = false;
    app.guest = N.createGuest(code, {
        state: (g, you, online) => {
            const prev = app.game, prevMe = app.me;
            app.game = g;
            app.me = you;
            app.online = online;
            if (prev && prev.id === g.id && you && prevMe === you) notice(prev, g, you);
            if (you && !prevMe) wake();
            Wallet.render(app);
        },
        status: (kind, detail) => {
            app.conn = kind;
            if (kind === 'connected' && !joinedOnce) { joinedOnce = true; stage('guest_joined'); }
            if (kind === 'notfound') diag('room_not_found', { why: String(detail || '') });
            Wallet.render(app);
        },
    });
}

/* A phone reacts to what happened to ITS money: a coin for money in, a lower
   note for money out, a ping for a request or its turn — and a short buzz,
   because the phone is usually face down next to the board. */
function notice(prev, g, me) {
    const r0 = L.replay(prev), r1 = L.replay(g);
    const d = (r1.bal[me] || 0) - (r0.bal[me] || 0);
    if (d) {
        let what = '';
        for (let i = g.log.length - 1; i >= 0; i--) {
            const e = g.log[i];
            if (e.void) continue;
            const ds = H.describe(g, e, r1);
            if (H.deltaFor(ds, me)) { what = ds.what + (ds.note ? ' · ' + ds.note : ''); break; }
        }
        app.lastDelta = { d, what };
        app.flashMe = true;
        sound(d > 0 ? 'in' : 'out');
        buzz(d > 0 ? 40 : 25);
    }
    const q0 = new Set(prev.requests.filter((x) => x.st === 'open' && x.from === me).map((x) => x.id));
    if (g.requests.some((x) => x.st === 'open' && x.from === me && !q0.has(x.id))) { sound('alert'); buzz([60, 60, 60]); }
    // my request answered «no»
    for (const x of g.requests) {
        const was = prev.requests.find((y) => y.id === x.id);
        if (was && was.st === 'open' && x.st === 'no' && x.to === me) app.say(t('w.declined', { who: H.name(g, x.from) }), 'warning');
    }
    const h0 = prev.poker && prev.poker.hand, h1 = g.poker && g.poker.hand;
    if (h1 && h1.toAct === me && !(h0 && h0.toAct === me && h0.n === h1.n && h0.street === h1.street)) { sound('turn'); buzz([80, 40, 80]); }
}

app.claim = async (pid) => {
    if (!app.guest) return;
    const r = await app.guest.claim(pid);
    if (!r.ok) app.say(r.err === 'seat_taken' ? t('err.seat_taken') : app.errText(r), 'warning');
};
app.changeSeat = () => { if (app.guest) { app.guest.leave(); app.me = null; Wallet.render(app); } };
app.leaveRoom = () => {
    if (app.guest) { app.guest.stop(); app.guest = null; }
    app.role = null;
    app.game = null;
    app.me = null;
    try { history.replaceState(null, '', location.pathname); } catch (e) { /* ignore */ }
    boot(true);
};

/* ── export ───────────────────────────────────────────────────────────────── */

function fileName(ext) {
    const d = new Date(app.game.created || Date.now());
    const day = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    const base = [t('file.base'), day];
    if (window.__outputName && typeof window.__outputName.join === 'function') {
        const n = window.__outputName.join(base, ext);
        if (n) return n;
    }
    return base.join('-').replace(/[\\/:*?"<>|]+/g, '_') + '.' + ext;
}

app.exportCsv = () => {
    const blob = new Blob([H.csv(app.game)], { type: 'text/csv;charset=utf-8' });
    if (typeof window.__downloadFile === 'function') window.__downloadFile(blob, fileName('csv'));
    complete('export', 'csv');
};

app.exportPdf = () => {
    if (typeof window.__generatePDF !== 'function') { app.say(t('err.pdf'), 'error'); return; }
    const paper = $('bgbPaper');
    const g = app.game;
    // the clone is taken from the paper node itself: its own styles, no positioning
    const sheet = H.sheet(g, g.ended ? Table.resultsNode(g, L.replay(g)) : null);
    put(clear(paper), sheet);
    const done = () => clear(paper);
    const p = window.__generatePDF({
        element: sheet,
        filename: fileName('pdf'),
        // a long history must break between rows, not push the table to page 2
        pagebreak: { mode: ['css', 'legacy'] },
        strings: { libError: t('err.pdf'), genError: t('err.pdf') },
    });
    if (p && p.then) p.then((res) => { done(); if (res && res.ok) complete('export', 'pdf'); }, done);
    else setTimeout(done, 1500);
};

/* ── misc controls ────────────────────────────────────────────────────────── */

app.toggleSound = () => {
    setSound(!soundEnabled());
    settingSave('sound', soundEnabled());
    if (soundEnabled()) sound('in');
    render();
};

app.fullscreen = () => {
    const el = $('bgbApp');
    if (document.fullscreenElement || document.webkitFullscreenElement) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (exit) { try { const p = exit.call(document); if (p && p.catch) p.catch(() => { /* already out */ }); } catch (e) { /* ignore */ } }
        return;
    }
    if (typeof window.__safeFullscreen === 'function') window.__safeFullscreen(el, { onDenied: () => app.say(t('ui.fs_denied'), 'info'), onUnsupported: () => app.say(t('ui.fs_denied'), 'info') });
};

app.onLevel = (lv) => {
    sound('level');
    buzz([100, 60, 100]);
    app.say(t('pk.level_up', { sb: lv.sb, bb: lv.bb }), 'info');
    render();
};

function wake() {
    try { if (window.__wakeLock && typeof window.__wakeLock.request === 'function') window.__wakeLock.request(); } catch (e) { /* the screen may sleep */ }
}

/* ── boot ─────────────────────────────────────────────────────────────────── */

function resumeBanner(g) {
    const box = $('bgbResume');
    const ago = Math.max(1, Math.round((Date.now() - (g.updated || g.created)) / 3600000));
    put(clear(box), 
        h('span', { class: 'bgb-resume-t' }, h('b', { text: t('ui.resume_title') }), ' ',
            t('ui.resume_d', { n: g.players.length, e: g.log.filter((e) => !e.void).length, h: ago })),
        h('span', { class: 'bgb-row' },
            h('button', { type: 'button', class: 'lg-btn lg-btn-sm', id: 'bgbResumeYes', text: t('ui.resume_yes'), on: { click: () => { resumeHost(g); wake(); } } }),
            h('button', { type: 'button', class: 'lg-btn lg-btn-ghost lg-btn-sm', text: t('ui.resume_no'), on: { click: () => { forget(); box.hidden = true; } } })));
    box.hidden = false;
}

function boot(fromLeave) {
    const params = new URLSearchParams(location.search);
    const raw = params.get('room');
    const RC = window.__RoomConnect;
    const code = raw && RC ? RC.validateCode(raw) : (raw && /^[a-z0-9]{6,8}$/i.test(raw) ? raw.toLowerCase() : null);
    const saved = load();
    if (code && !fromLeave) {
        if (saved && saved.room && saved.room.code === code) { resumeHost(saved); return; }
        joinRoom(code);
        return;
    }
    if (saved && !fromLeave && Date.now() - (saved.updated || saved.created || 0) < RESUME_MS && !saved.ended) {
        resumeHost(saved);
        return;
    }
    Setup.render(app);
    show('setup');
    stage('setup');
    if (saved && (saved.log.length || saved.ended)) resumeBanner(saved);
}

function wireJoinBox() {
    const inp = $('bgbJoinCode');
    const btn = $('bgbJoinBtn');
    const err = $('bgbJoinErr');
    if (!inp || !btn) return;
    const go = () => {
        const RC = window.__RoomConnect;
        const res = RC ? RC.validateCodeDetailed(inp.value) : { code: null, error: 'format' };
        if (res.error) { err.hidden = false; err.textContent = res.error === 'empty' ? t('join.empty') : t('join.bad'); return; }
        err.hidden = true;
        joinRoom(res.code);
    };
    btn.addEventListener('click', go);
    inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); go(); } });
}

function init() {
    setSound(settingLoad('sound', true) !== false);
    S.init();
    wireJoinBox();
    document.addEventListener('keydown', (e) => {
        if (app.role !== 'host' || app.dialogOpen()) return;
        const tg = e.target;
        if (tg && (tg.tagName === 'INPUT' || tg.tagName === 'TEXTAREA' || tg.tagName === 'SELECT' || tg.isContentEditable)) return;
        if ((e.key === 'z' || e.key === 'Z' || e.key === 'я' || e.key === 'Я') && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); app.undo(); }
        else if ((e.key === 'z' || e.key === 'Z') && (e.ctrlKey || e.metaKey)) { e.preventDefault(); app.undo(); }
    });
    // the first touch of a resumed game re-takes the screen lock (Safari wants a gesture)
    document.addEventListener('pointerdown', function once() { if (app.role) wake(); document.removeEventListener('pointerdown', once); }, { passive: true });
    setInterval(() => Table.tick(app), 500);
    try { boot(false); } catch (e) {
        // a broken saved game must not leave the page greyed out forever
        diag('boot_failed', { msg: String(e && e.message || e).slice(0, 120) });
        forget();
        Setup.render(app);
        show('setup');
    } finally {
        $('bgbApp').removeAttribute('data-loading');
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

export { app, SLUG };
